import { useState, useEffect, useRef } from 'react';
import { getTasks } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Download, Search, Loader2, ChevronDown, X } from 'lucide-react';

function mapPieceType(pieceType) {
  if (!pieceType) return 'Ring';
  const map = {
    ring: 'Ring', necklace: 'Necklace', pendant: 'Pendant',
    earring: 'Earring', bangle: 'Bangle', bracelet: 'Bracelet',
    chain: 'Chain',
  };
  return map[pieceType.toLowerCase()] || 'Other';
}

function parseColors(colorStr) {
  if (!colorStr) return ['Yellow'];
  // Check for text inside parentheses first, e.g., "Two-Tone (White,Yellow)" or "(White & Yellow)"
  const parenMatch = colorStr.match(/\(([^)]+)\)/);
  let content = colorStr;
  if (parenMatch) {
    content = parenMatch[1];
  }
  // Now split the content by common delimiters: comma, slash, ampersand, plus, and the word "and"
  let normalized = content
    .replace(/\//g, ',')
    .replace(/&/g, ',')
    .replace(/\+/g, ',')
    .replace(/\band\b/gi, ',');
  
  // Split by comma and clean up whitespace
  const colors = normalized
    .split(',')
    .map(c => c.trim())
    .filter(c => c.length > 0 && !/two[- ]tone/i.test(c) && !/three[- ]tone/i.test(c) && !/multi[- ]tone/i.test(c));
  
  if (colors.length === 0) {
    if (!/^(two|three|multi)[- ]tone$/i.test(colorStr.trim())) {
      const clean = colorStr.trim();
      return [clean.charAt(0).toUpperCase() + clean.slice(1).toLowerCase()];
    }
    return ['Yellow'];
  }
  return colors.map(c => c.charAt(0).toUpperCase() + c.slice(1).toLowerCase());
}

export function mapTaskToSheet(task) {
  const metals = [];
  if (task.metal_details && task.metal_details.length > 0) {
    task.metal_details.forEach(m => {
      const karat = m.karat || m.metal_karat || task.metal_karat || '14K';
      const color = m.colour || m.color || m.metal_colour || task.metal_colour || 'Yellow';
      const weight = m.netWeight || m.net_weight || m.weight || '';
      
      const colors = parseColors(color);
      colors.forEach(col => {
        metals.push({
          id: crypto.randomUUID(),
          metal_type: (m.metal_type || task.metal_type || 'gold').toLowerCase(),
          karat: karat.replace(/K$/i, '') + 'K', // Normalize to e.g. "14K"
          color: col,
          netWeight: colors.length > 1 ? '' : String(weight),
        });
      });
    });
  }
  // Fallback: use flat task fields if metal_details is empty
  if (metals.length === 0 && (task.metal_karat || task.net_weight)) {
    const karat = task.metal_karat || '14K';
    const color = task.metal_colour || 'Yellow';
    const weight = task.net_weight || '';
    
    const colors = parseColors(color);
    colors.forEach(col => {
      metals.push({
        id: crypto.randomUUID(),
        metal_type: (task.metal_type || 'gold').toLowerCase(),
        karat: karat.replace(/K$/i, '') + 'K',
        color: col,
        netWeight: colors.length > 1 ? '' : String(weight),
      });
    });
  }
  if (metals.length === 0) {
    metals.push({ id: crypto.randomUUID(), metal_type: 'gold', karat: '14K', color: 'Yellow', netWeight: '' });
  }

  // Use canonical CAD/backend field names (stone_category / ownership / stone_shape /
  // category / stone_quality / size_mm / weight_per_stone / quantity) so round-tripping
  // through the CAD sheet preserves the values without dropping ownership etc.
  const stones = [];
  if (task.diamonds && task.diamonds.length > 0) {
    task.diamonds.forEach(d => {
      stones.push({
        id: crypto.randomUUID(),
        stone_category: d.stone_category || 'Natural',
        ownership: d.ownership || 'KS Goods',
        stone_shape: d.stone_shape || 'Round',
        category: d.category || 'Accent Stone',
        stone_quality: d.stone_quality || '',
        setting_type: d.setting_type || '',
        size_mm: d.size_mm || d.stone_size_mm || '',
        weight_per_stone: String(d.weight_per_stone ?? d.stone_weight_ct ?? ''),
        quantity: String(d.quantity ?? d.pieces ?? ''),
      });
    });
  }
  if (task.other_stones && task.other_stones.length > 0) {
    task.other_stones.forEach(s => {
      stones.push({
        id: crypto.randomUUID(),
        stone_category: 'Colour Stone',
        ownership: 'KS Goods',
        stone_shape: s.shape || 'Round',
        category: 'Accent Stone',
        stone_quality: '',
        setting_type: s.setting_type || '',
        size_mm: s.size_mm || '',
        weight_per_stone: String(s.weight_ct || ''),
        quantity: String(s.pieces || ''),
      });
    });
  }

  const netWeight = metals.reduce((sum, m) => sum + (parseFloat(m.netWeight) || 0), 0);

  return {
    styleNo: task.style_no || '',
    jewelryType: mapPieceType(task.piece_type),
    customerName: task.client_name || '',
    customerDesignNo: task.customer_design_no || task.party_cad_no || '',
    metals,
    netWeight: netWeight > 0 ? netWeight.toFixed(2) : '',
    grossWeight: task.gross_weight ? String(task.gross_weight) : '',
    grossWeightManual: !!task.gross_weight,
    size: task.size ? String(task.size) : '',
    date: new Date().toISOString().split('T')[0],
    designerName: task.designer_name || '',
    stones,
    findings: [],
    otherDetails: [task.special_instructions, task.notes].filter(Boolean).join('\n') || '',
    ksNotes: task.ks_notes || '',
    images: [
      { id: 0, file: null, url: '', label: 'Front View', annotations: [], textBoxes: [], highlights: [] },
      { id: 1, file: null, url: '', label: 'Top View', annotations: [], textBoxes: [], highlights: [] },
      { id: 2, file: null, url: '', label: 'Side View', annotations: [], textBoxes: [], highlights: [] },
      { id: 3, file: null, url: '', label: 'Perspective View', annotations: [], textBoxes: [], highlights: [] },
    ],
    finishing: task.finishing || task.texture || '',
    engravingInstructions: task.engraving || '',
    engravingImage: task.engraving_image ? { id: 0, file: null, url: task.engraving_image, label: 'Engraving Reference' } : null,
    stampingInstructions: task.stamping_instructions || '',
    stampingImages: (task.stamping_images || []).map((url, i) => ({ id: i, file: null, url, label: 'Stamping Reference' })),
    customerImages: [],
  };
}

export function LoadFromTaskButton({ onLoadSheet, compact = false }) {
  const [open, setOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (open && tasks.length === 0) {
      setLoading(true);
      getTasks()
        .then(res => {
          const list = Array.isArray(res.data) ? res.data : res.data?.tasks || [];
          setTasks(list);
        })
        .catch(() => setTasks([]))
        .finally(() => setLoading(false));
    }
  }, [open, tasks.length]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const filtered = tasks.filter(t => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (t.style_no || '').toLowerCase().includes(q)
      || (t.client_name || '').toLowerCase().includes(q)
      || (t.product_name || '').toLowerCase().includes(q)
      || (t.internal_cad_no || '').toLowerCase().includes(q);
  });

  const handleSelect = (task) => {
    const sheetData = mapTaskToSheet(task);
    onLoadSheet(sheetData, task.id);
    setOpen(false);
    setSearch('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant={compact ? "ghost" : "outline"}
        size={compact ? "icon" : "sm"}
        onClick={() => setOpen(!open)}
        className={compact ? "h-8 w-8" : "h-7 text-xs"}
        title={compact ? "Load from Task" : undefined}
        data-testid="cad-load-from-task"
      >
        <Download className={compact ? "h-4 w-4" : "h-3.5 w-3.5 mr-1"} />
        {!compact && <>Load from Task<ChevronDown className="h-3 w-3 ml-1" /></>}
      </Button>

      {open && (
        <div className={`absolute ${compact ? 'left-full ml-1 top-0' : 'right-0 top-full mt-1'} w-96 bg-card border border-border rounded-lg shadow-xl z-50 overflow-hidden`}>
          <div className="p-2 border-b border-border flex items-center gap-2">
            <Search className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
            <Input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by Style No, Client, or CAD No..."
              className="h-7 text-xs border-0 shadow-none focus-visible:ring-0 p-0"
              data-testid="cad-load-task-search"
            />
            {search && (
              <button onClick={() => setSearch('')} className="text-muted-foreground hover:text-foreground">
                <X className="h-3 w-3" />
              </button>
            )}
          </div>

          <div className="max-h-72 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-6 text-center text-xs text-muted-foreground">
                {tasks.length === 0 ? 'No tasks found' : 'No matching tasks'}
              </div>
            ) : (
              filtered.map(task => (
                <button
                  key={task.id}
                  onClick={() => handleSelect(task)}
                  className="w-full text-left px-3 py-2 hover:bg-muted/50 transition-colors border-b border-border/30 last:border-b-0"
                  data-testid={`cad-load-task-${task.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="truncate">
                      <span className="text-xs font-bold text-foreground">{task.style_no || task.internal_cad_no || 'Untitled'}</span>
                      {task.party_cad_no && <span className="text-[10px] text-muted-foreground ml-1.5">({task.party_cad_no})</span>}
                    </div>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground flex-shrink-0 ml-2">
                      {task.piece_type || 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    {task.client_name && <span className="text-[11px] text-muted-foreground truncate">{task.client_name}</span>}
                    {task.product_name && <span className="text-[11px] text-muted-foreground truncate">{'\u2022'} {task.product_name}</span>}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    {task.metal_karat && `${task.metal_karat} ${task.metal_colour || ''}`}
                    {task.total_diamond_weight_ct && ` \u2022 ${task.total_diamond_weight_ct}ct`}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
